<script>
import { mapState } from 'vuex'
import date from 'locutus/php/datetime/date'
import long2ip from 'locutus/php/network/long2ip'
import strtotime from 'locutus/php/datetime/strtotime'
import PageNavigation from '@components/admin/page-navigation'
import ActivityLogVars from '@components/admin/activity-log-vars'
import { fetchLogList } from '@apis/admin/log'
import fromPairs from 'lodash/fromPairs'
import map from 'lodash/map'
import mapKeys from 'lodash/mapKeys'
import pick from 'lodash/pick'

export default {
  components: { PageNavigation, ActivityLogVars },
  data() {
    return {
      loading: false,
      list: [],
      total_count: 0,
      page: 1,
      total_page: 0,
      page_navigation: {},
      search_params: {},
      logset_log_counts: {},
      s_log_seq: null,
      show_log_vars: false,
      excel_headers: [
        ['seq', this.$t('activity_log_list.seq')],
        ['eventer_seq', this.$t('activity_log_list.eventer_seq')],
        ['description', this.$t('activity_log_list.description')],
        ['ipaddress', this.$t('activity_log_list.ipaddress')],
        ['user_agent', this.$t('activity_log_list.user_agent')],
        ['default_locale', this.$t('activity_log_list.default_locale')],
        ['regdate', this.$t('activity_log_list.regdate')],
        ['logset_seq', this.$t('activity_log_list.logset_seq')],
        ['target', this.$t('activity_log_list.target')],
        ['target_seq', this.$t('activity_log_list.target_seq')],
        ['reference', this.$t('activity_log_list.reference')],
        ['reference_seq', this.$t('activity_log_list.reference_seq')],
        ['logset_description', this.$t('activity_log_list.logset_description')],
        ['user_id', this.$t('activity_log_list.user_id')],
        ['email_address', this.$t('activity_log_list.email_address')],
        ['user_name', this.$t('activity_log_list.user_name')],
        [
          'logset_description_replaced',
          this.$t('activity_log_list.logset_description_replaced'),
        ],
        [
          'description_replaced',
          this.$t('activity_log_list.description_replaced'),
        ],
      ],
      pickerOptions: {
        shortcuts: [
          {
            text: this.$t('admin.shortcuts_today'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('today') * 1000),
                new Date(strtotime('tomorrow') * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('admin.shortcuts_yesterday'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('yesterday') * 1000),
                new Date(strtotime('today') * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('admin.shortcuts_thismonth'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(
                  strtotime('today', strtotime('first day of this month')) *
                    1000
                ),
                new Date(
                  strtotime('today', strtotime('first day of next month')) *
                    1000 -
                    1
                ),
              ])
            },
          },
          {
            text: this.$t('admin.shortcuts_lastmonth'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(
                  strtotime('today', strtotime('first day of last month')) *
                    1000
                ),
                new Date(
                  strtotime('today', strtotime('first day of this month')) *
                    1000 -
                    1
                ),
              ])
            },
          },
          {
            text: this.$t('admin.shortcuts_thisweek'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('today', strtotime('this week')) * 1000),
                new Date(strtotime('today', strtotime('next week')) * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('admin.shortcuts_lastweek'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('today', strtotime('last week')) * 1000),
                new Date(strtotime('today', strtotime('this week')) * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('admin.shortcuts_week'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('-7 day') * 1000),
                new Date(),
              ])
            },
          },
          {
            text: this.$t('admin.shortcuts_month'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('-30 day') * 1000),
                new Date(),
              ])
            },
          },
        ],
      },
    }
  },
  computed: {
    ...mapState('common', {
      constants: (state) => state.constants,
    }),
    c_regdate_range: {
      get() {
        const regdate_start = this.search_params.regdate_start || ''
        const regdate_end = this.search_params.regdate_end || ''
        return [
          regdate_start ? new Date(regdate_start * 1) : '',
          regdate_end ? new Date(regdate_end * 1) : '',
        ]
      },
      set([start, end]) {
        this.$set(
          this.search_params,
          'regdate_start',
          start ? `${start.getTime()}` : undefined
        )
        this.$set(
          this.search_params,
          'regdate_end',
          end ? `${end.getTime()}` : undefined
        )
      },
    },
  },
  watch: {
    $route(to, from) {
      this.page = this.$route.query.page || 1
      this.loadList().catch((e) => this.$toasted.error(e.message))
    },
  },
  mounted() {
    this.init()
  },
  methods: {
    init() {
      this.page = this.$route.query.page || 1
      this.search_params = { ...this.$route.query }
      Promise.all([this.loadList()])
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadList() {
      this.loading = true
      const ret_obj = await fetchLogList({
        ...this.$route.params,
        ...this.$route.query,
      })

      const logset_log_counts = {}
      let cur_logset_seq = 0
      ret_obj.data = map(ret_obj.data, (log) => {
        if (cur_logset_seq !== log.logset_seq) {
          log.is_first = true
          cur_logset_seq = log.logset_seq
        } else {
          log.is_first = false
        }

        logset_log_counts[log.logset_seq] =
          logset_log_counts[log.logset_seq] || 0
        logset_log_counts[log.logset_seq]++

        return log
      })

      this.list = ret_obj.data
      this.total_count = ret_obj.total_count
      this.total_page = ret_obj.total_page
      this.page_navigation = ret_obj.page_navigation
      this.logset_log_counts = logset_log_counts
    },
    search() {
      this.$router.push({ query: this.search_params })
    },
    handleShowLogVars(log_seq) {
      this.s_log_seq = log_seq
      this.show_log_vars = true
    },
    async handleExportExcel() {
      this.loading = true
      try {
        if (
          !(await this.$confirm(
            this.$t('admin.msg_excel_warning'),
            this.$t('admin.modal_confirm'),
            {
              type: 'warning',
            }
          ).catch(() => false))
        )
          return

        const { data } = await fetchLogList({
          ...this.$route.params,
          ...this.$route.query,
          no_paging: 1,
        })

        const { default: exportJsonToExcel } = await import(
          '@utils/export-json-to-excel'
        )
        exportJsonToExcel({
          header: map(this.excel_headers, ([, title]) => title),
          data: this.format2excel(data),
          filename: `$t('activity_log_list.activity_history') ${date(
            'YmdHis'
          )}`,
        })
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    format2excel(list) {
      const excel_fields = map(this.excel_headers, ([name]) => name)
      return map(list, (data) => {
        let result = pick(data, excel_fields)
        const keys = Object.keys(result)
        result = mapKeys(
          map(result, (val, key) => {
            switch (key) {
              case 'target':
              case 'reference':
                return fromPairs(this.constants.log.log.target)[val]
              case 'regdate':
                return val ? date('Y-m-d H:i:s', new Date(val)) : ''
              default:
                return val
            }
          }),
          (val, key) => {
            return keys[key]
          }
        )

        return map(excel_fields, (key) => result[key])
      })
    },
    date,
    fromPairs,
    long2ip,
  },
}
</script>

<template>
  <div v-loading="loading">
    <h3 :class="$style.xeAdmin">{{ $t('admin.search') }}</h3>
    <form @submit.prevent="search">
      <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
        <colgroup>
          <col width="20%" />
          <col width="30%" />
          <col width="20%" />
          <col width="30%" />
        </colgroup>
        <tbody>
          <tr>
            <td colspan="4">
              <el-date-picker
                v-model="c_regdate_range"
                :class="$style.fr"
                type="datetimerange"
                :picker-options="pickerOptions"
                range-separator="To"
                start-placeholder="Start date"
                end-placeholder="End date"
                align="right"
              >
              </el-date-picker>
            </td>
          </tr>
          <tr>
            <th scope="col">
              <div>
                <select
                  v-model="search_params.search_target"
                  :class="$style.fullWidth"
                >
                  <option
                    v-for="val in [
                      [undefined, $t('admin.search_target')],
                      ['user_id', $t('admin.user_id')],
                      ['email_address', $t('admin.email_address')],
                      ['user_name', $t('admin.user_name')],
                      ['ipaddress', $t('admin.ipaddress')],
                      ['user_agent', $t('admin.user_agent')],
                      ['regdate_more', $t('admin.login_log_regdate_more')],
                      ['regdate_less', $t('admin.login_log_regdate_less')],
                    ]"
                    :key="val[1]"
                    :value="val[0]"
                    >{{ val[1] }}</option
                  >
                </select>
              </div>
            </th>
            <td>
              <input
                v-model="search_params.search_keyword"
                :placeholder="$t('admin.search_word')"
                type="text"
                :class="[$style.inputTypeText, $style.fullWidth]"
              />
            </td>
            <td colspan="2">
              <ElCheckbox
                v-model="search_params.use_statistics_exclude"
                :false-label="undefined"
                true-label="N"
                >{{ $t('admin.use_statistics_exclude') }}</ElCheckbox
              >
              <ElCheckbox
                v-model="search_params.member_is_alive"
                :false-label="undefined"
                true-label="Y"
                >{{ $t('admin.member_is_alive') }}</ElCheckbox
              >
            </td>
          </tr>
          <tr>
            <th scope="col"
              ><div>{{ $t('admin.target') }}</div></th
            >
            <td>
              <el-select
                v-model="search_params.target"
                filterable
                :placeholder="$t('admin.total')"
              >
                <el-option
                  v-for="val in [
                    [undefined, $t('admin.total')],
                    ...constants.log.log.target,
                  ]"
                  :key="val[0]"
                  :value="val[0]"
                  :label="val[1]"
                >
                </el-option>
              </el-select>
            </td>
            <th scope="col"
              ><div>{{ $t('admin.target_number') }}</div></th
            >
            <td>
              <input
                v-model="search_params.target_seq"
                type="text"
                :class="[$style.inputTypeText, $style.w80]"
              />
            </td>
          </tr>
          <tr>
            <th scope="col"
              ><div>{{ $t('admin.connect') }}</div></th
            >
            <td>
              <el-select
                v-model="search_params.reference"
                filterable
                :placeholder="$t('admin.total')"
              >
                <el-option
                  v-for="val in [
                    [undefined, $t('admin.total')],
                    ...constants.log.log.target,
                  ]"
                  :key="val[0]"
                  :value="val[0]"
                  :label="val[1]"
                >
                </el-option>
              </el-select>
            </td>
            <th scope="col"
              ><div>{{ $t('admin.connection_number') }}</div></th
            >
            <td>
              <input
                v-model="search_params.reference_seq"
                type="text"
                :class="[$style.inputTypeText, $style.w80]"
              />
            </td>
          </tr>
          <tr>
            <th scope="col"
              ><div>{{ $t('admin.sort') }}</div></th
            >
            <td>
              <select v-model="search_params.sort_order">
                <option
                  v-for="val in [
                    [undefined, $t('admin.regdate')],
                    ['last_login', $t('admin.last_login')],
                  ]"
                  :key="val[1]"
                  :value="val[0]"
                  >{{ val[1] }}</option
                >
              </select>
              <select v-model="search_params.order_type">
                <option
                  v-for="val in [
                    [undefined, $t('admin.desc')],
                    ['asc', $t('admin.asc')],
                  ]"
                  :key="val[1]"
                  :value="val[0]"
                  >{{ val[1] }}</option
                >
              </select>
            </td>
          </tr>
          <tr>
            <th colspan="4" :class="$style.button">
              <a
                href="#"
                :class="[$style.button, $style.red]"
                @click.prevent="
                  search_params = {}
                  search
                "
                ><span>{{ $t('admin.reset') }}</span></a
              >
              {{ ' ' }}
              <span :class="[$style.button, $style.black]"
                ><input type="submit" :value="$t('admin.submit')"
              /></span>
            </th>
          </tr>
        </tbody>
      </table>
    </form>
    <h3 :class="$style.xeAdmin">{{ $t('admin.activity_log') }}</h3>
    <fieldset :class="$style.clearfix">
      <div :class="$style.fl">
        <a href="#" :class="[$style.button]" @click.prevent="handleExportExcel"
          ><span>{{ $t('admin.excel_download') }}</span></a
        >
      </div>
    </fieldset>
    <table cellspacing="0" cellpadding="0" :class="$style.adminTable">
      <caption v-if="!loading"
        >Total {{ total_count }}, Page {{ page }}/{{ total_page }}</caption
      >
      <thead>
        <tr>
          <th scope="col" rowspan="2" :class="$style.quarter_wide"
            ><div>{{ $t('admin.cmd_brokerage_page') }}</div></th
          >
          <th scope="col" rowspan="2" :class="$style.quarter_wide"
            ><div>{{ $t('admin.performer') }}</div></th
          >
          <th scope="col" rowspan="2" :class="$style.quarter_wide"
            ><div>{{ $t('admin.perfor_ip') }}</div></th
          >
          <th scope="col" rowspan="2" :class="$style.quarter_wide"
            ><div>{{ $t('admin.case_name') }}</div></th
          >
          <th scope="col" rowspan="2" :class="$style.quarter_wide"
            ><div>{{ $t('admin.occurrence_date') }}</div></th
          >
          <th scope="col" rowspan="2"
            ><div
              ><a href="#">{{ $t('admin.num') }}</a></div
            ></th
          >
          <th scope="col" :class="$style.quarter_wide"
            ><div>{{ $t('admin.target') }}</div></th
          >
          <th scope="col" :class="$style.quarter_wide"
            ><div>{{ $t('admin.target_number') }}</div></th
          >
          <th scope="col" :class="$style.quarter_wide"
            ><div>{{ $t('admin.connect') }}</div></th
          >
          <th scope="col" :class="$style.quarter_wide"
            ><div>{{ $t('admin.connection_number') }}</div></th
          >
          <th scope="col" rowspan="2"><div>Action</div></th>
        </tr>
        <tr>
          <th scope="col" colspan="4"
            ><div>{{ $t('admin.explanation') }}</div></th
          >
        </tr>
      </thead>
      <tbody>
        <template v-for="data in list">
          <tr :key="`${data.seq}-1`">
            <template v-if="data.is_first">
              <td
                :class="[$style.center, $style.nowrap]"
                :rowspan="logset_log_counts[data.logset_seq] * 2"
                >{{
                  data.brokerage_seq
                    ? data.brokerage_title
                    : $t('admin.no_appoint')
                }}</td
              >
              <td
                :class="[$style.center, $style.nowrap]"
                :rowspan="logset_log_counts[data.logset_seq] * 2"
                >{{
                  data.eventer_seq == -1
                    ? $t('admin.system_boxmark')
                    : data.user_id
                }}</td
              >
              <td
                :class="[$style.center, $style.nowrap]"
                :rowspan="logset_log_counts[data.logset_seq] * 2"
                >{{ long2ip(data.ipaddress) }}</td
              >
              <td
                :class="[$style.nowrap, $style.wide]"
                :rowspan="logset_log_counts[data.logset_seq] * 2"
                >{{ data.logset_description_replaced }}</td
              >
              <td
                :class="[$style.center, $style.nowrap]"
                :rowspan="logset_log_counts[data.logset_seq] * 2"
                >{{ date('Y-m-d H:i:s', new Date(data.regdate)) }}</td
              >
            </template>
            <td
              :class="[
                { [$style.row2]: data._no % 2 },
                $style.center,
                $style.nowrap,
              ]"
              rowspan="2"
              >{{ data._no }}</td
            >
            <td
              :class="[
                { [$style.row2]: data._no % 2 },
                $style.center,
                $style.nowrap,
              ]"
              >{{ fromPairs(constants.log.log.target)[data.target] }}</td
            >
            <td :class="[{ [$style.row2]: data._no % 2 }, $style.center]">{{
              data.target_seq
            }}</td>
            <td
              :class="[
                { [$style.row2]: data._no % 2 },
                $style.center,
                $style.nowrap,
              ]"
              >{{ fromPairs(constants.log.log.target)[data.reference] }}</td
            >
            <td :class="[{ [$style.row2]: data._no % 2 }, $style.center]">{{
              data.reference_seq
            }}</td>
            <td
              rowspan="2"
              :class="[
                { [$style.row2]: data._no % 2 },
                $style.center,
                $style.nowrap,
              ]"
            >
              <a
                href="#"
                :class="[$style.button, $style.small]"
                @click.prevent="handleShowLogVars(data.seq)"
                ><span>{{ $t('admin.value_show') }}</span></a
              >
            </td>
          </tr>
          <tr :key="`${data.seq}-2`">
            <td :class="[{ [$style.row2]: data._no % 2 }]" colspan="4">{{
              data.description
                ? data.description_replaced
                : $t('admin.not_recorded_boxmark')
            }}</td>
          </tr>
        </template>
        <tr v-if="loading">
          <td :colspan="9" :class="$style.tCenter">{{
            $t('admin.loading')
          }}</td>
        </tr>
        <tr v-else-if="!list.length">
          <td :colspan="10" :class="[$style.tCenter, $style.center]">{{
            $t('admin.msg_search_activity_list')
          }}</td>
        </tr>
      </tbody>
    </table>
    <PageNavigation
      v-if="!loading"
      :total_count="total_count"
      :total_page="page_navigation.total_page"
      :cur_page="page"
      :page_count="page_navigation.page_count"
    ></PageNavigation>
    <ElDialog :title="$t('admin.value_show')" :visible.sync="show_log_vars">
      <ActivityLogVars :key="s_log_seq" :log_seq="s_log_seq"></ActivityLogVars>
    </ElDialog>
  </div>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';

.crossTable th,
.crossTable td {
  vertical-align: middle;
}
</style>
